/**
 * Recurring Schedules Hook
 * Feature: 005-clinic-scheduling
 *
 * CRUD operations for recurring schedules with session occurrence generation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import {
  addWeeks,
  addMonths,
  nextDay,
  isAfter,
  isBefore,
  format,
  startOfDay,
  addDays,
} from 'date-fns'
import type {
  RecurringSchedule,
  CreateRecurringScheduleInput,
  UpdateRecurringScheduleInput,
  DeactivateRecurringScheduleInput,
  ScheduleOperationResult,
  DayOfWeek,
  Frequency,
} from '../types/scheduling.types'

function normalizeLocationText(
  value: string | null | undefined
): string | null {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

// ============================================================
// Occurrence Generation Utility
// ============================================================

const GENERATION_WINDOW_MONTHS = 3

/**
 * Generate session dates for a recurring schedule within a rolling window.
 * Maps DayOfWeek (0=Sun..6=Sat) to date-fns day constants.
 */
function generateOccurrenceDates(
  dayOfWeek: DayOfWeek,
  frequency: Frequency,
  effectiveFrom: Date,
  effectiveUntil: Date | null,
  windowEndOverride?: Date
): Date[] {
  const today = startOfDay(new Date())
  const startDate = isAfter(effectiveFrom, today) ? effectiveFrom : today
  const windowEnd =
    windowEndOverride ?? addMonths(today, GENERATION_WINDOW_MONTHS)
  const endDate =
    effectiveUntil && isBefore(effectiveUntil, windowEnd)
      ? effectiveUntil
      : windowEnd

  const dates: Date[] = []

  // Find the first occurrence of the target day_of_week on or after startDate
  let current = startOfDay(startDate)
  const targetDay = dayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6
  if (current.getDay() !== targetDay) {
    current = nextDay(current, targetDay)
  }

  while (!isAfter(current, endDate)) {
    dates.push(current)

    switch (frequency) {
      case 'weekly':
        current = addWeeks(current, 1)
        break
      case 'biweekly':
        current = addWeeks(current, 2)
        break
      case 'monthly':
        current = addMonths(current, 1)
        // Ensure we land on the correct day_of_week after month skip
        if (current.getDay() !== targetDay) {
          current = nextDay(addDays(current, -7), targetDay)
        }
        break
    }
  }

  return dates
}

// ============================================================
// Fetch Function
// ============================================================

async function fetchRecurringSchedules(
  organizationId: string,
  patientId?: string
): Promise<RecurringSchedule[]> {
  let query = supabase
    .from('recurring_schedules')
    .select(
      `
      *,
      patient:patients (id, full_name),
      professionals:schedule_professionals (
        id,
        recurring_schedule_id,
        organization_id,
        professional_id,
        created_at,
        professional:profiles!schedule_professionals_professional_fkey (
          id,
          full_name
        )
      )
    `
    )
    .eq('organization_id', organizationId)
    .order('day_of_week', { ascending: true })
    .order('start_time', { ascending: true })

  if (patientId) {
    query = query.eq('patient_id', patientId)
  }

  const { data, error } = await query

  if (error) throw error

  const schedules = data ?? []
  const professionalIds = Array.from(
    new Set(
      schedules.flatMap((schedule) =>
        (schedule.professionals ?? [])
          .map((sp: Record<string, unknown>) => {
            const professional = sp.professional as Record<
              string,
              unknown
            > | null
            return typeof professional?.id === 'string' ? professional.id : null
          })
          .filter((id: string | null): id is string => id !== null)
      )
    )
  )

  const specialtyByProfessionalId = new Map<string, string>()

  if (professionalIds.length > 0) {
    const { data: memberSpecialties, error: memberSpecialtiesError } =
      await supabase
        .from('organization_members')
        .select('profile_id, specialty')
        .eq('organization_id', organizationId)
        .in('profile_id', professionalIds)

    if (memberSpecialtiesError) throw memberSpecialtiesError

    for (const member of memberSpecialties ?? []) {
      specialtyByProfessionalId.set(
        member.profile_id,
        member.specialty?.trim() || 'Profissional de Saúde'
      )
    }
  }

  return schedules.map((schedule) => ({
    ...schedule,
    professionals: (schedule.professionals ?? []).map(
      (sp: Record<string, unknown>) => ({
        ...sp,
        professional: sp.professional
          ? {
              id: (sp.professional as Record<string, unknown>).id as string,
              full_name: (sp.professional as Record<string, unknown>)
                .full_name as string,
              specialty:
                specialtyByProfessionalId.get(
                  (sp.professional as Record<string, unknown>).id as string
                ) ?? 'Profissional de Saúde',
            }
          : undefined,
      })
    ),
  })) as RecurringSchedule[]
}

// ============================================================
// Session Generation
// ============================================================

async function generateSessionOccurrences(
  schedule: {
    id: string
    organization_id: string
    patient_id: string
    day_of_week: DayOfWeek
    start_time: string
    end_time: string
    frequency: Frequency
    effective_from: string
    effective_until: string | null
    location_text: string | null
  },
  professionalIds: string[]
): Promise<void> {
  const effectiveFrom = new Date(schedule.effective_from)
  const effectiveUntil = schedule.effective_until
    ? new Date(schedule.effective_until)
    : null

  const dates = generateOccurrenceDates(
    schedule.day_of_week,
    schedule.frequency,
    effectiveFrom,
    effectiveUntil
  )

  if (dates.length === 0) return

  const primaryProfessionalId = professionalIds[0]

  // Check which dates already have sessions for this recurring_schedule_id
  const { data: existingSessions } = await supabase
    .from('sessions')
    .select('session_date')
    .eq('recurring_schedule_id', schedule.id)
    .in(
      'session_date',
      dates.map((d) => format(d, 'yyyy-MM-dd'))
    )

  const existingDates = new Set(
    (existingSessions ?? []).map((s) => s.session_date)
  )

  const newSessions = dates
    .filter((d) => !existingDates.has(format(d, 'yyyy-MM-dd')))
    .map((date) => ({
      organization_id: schedule.organization_id,
      patient_id: schedule.patient_id,
      professional_id: primaryProfessionalId,
      session_date: format(date, 'yyyy-MM-dd'),
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      session_type: 'Atendimento',
      status: 'scheduled' as const,
      location_text: schedule.location_text,
      recurring_schedule_id: schedule.id,
      is_exception: false,
    }))

  if (newSessions.length === 0) return

  const { data: insertedSessions, error: insertError } = await supabase
    .from('sessions')
    .insert(newSessions)
    .select('id')

  if (insertError) throw insertError

  // Add additional professionals to session_professionals
  if (professionalIds.length > 1 && insertedSessions) {
    const sessionProfessionals = insertedSessions.flatMap((session) =>
      professionalIds.slice(1).map((profId) => ({
        session_id: session.id,
        professional_id: profId,
      }))
    )

    const { error: spError } = await supabase
      .from('session_professionals')
      .insert(sessionProfessionals)

    if (spError) throw spError
  }
}

// ============================================================
// Hooks
// ============================================================

export function useRecurringSchedules(patientId?: string) {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['recurring-schedules', organizationId, patientId],
    queryFn: () => fetchRecurringSchedules(organizationId!, patientId),
    enabled: !!organizationId,
  })
}

export function useCreateRecurringSchedule() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (
      input: CreateRecurringScheduleInput
    ): Promise<ScheduleOperationResult<RecurringSchedule>> => {
      const organizationId = currentOrganization!.id

      // 1. Create the recurring schedule
      const { data: schedule, error: scheduleError } = await supabase
        .from('recurring_schedules')
        .insert({
          organization_id: organizationId,
          patient_id: input.patient_id,
          day_of_week: input.day_of_week,
          start_time: input.start_time,
          end_time: input.end_time,
          frequency: input.frequency,
          effective_from: input.effective_from,
          effective_until: input.effective_until ?? null,
          location_text: normalizeLocationText(input.location_text),
        })
        .select()
        .single()

      if (scheduleError) {
        return { success: false, error: scheduleError.message }
      }

      // 2. Add professionals to the junction table
      const scheduleProfessionals = input.professional_ids.map((profId) => ({
        recurring_schedule_id: schedule.id,
        professional_id: profId,
      }))

      const { error: profError } = await supabase
        .from('schedule_professionals')
        .insert(scheduleProfessionals)

      if (profError) {
        return { success: false, error: profError.message }
      }

      // 3. Generate session occurrences
      try {
        await generateSessionOccurrences(
          { ...schedule, organization_id: organizationId },
          input.professional_ids
        )
      } catch (genError) {
        console.error('Failed to generate occurrences:', genError)
        // Schedule was created successfully, so we return success
        // but log the generation failure
      }

      return { success: true, data: schedule as RecurringSchedule }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] })
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

export function useUpdateRecurringSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: UpdateRecurringScheduleInput
    ): Promise<ScheduleOperationResult<RecurringSchedule>> => {
      const { id, scope, professional_ids, ...updateData } = input

      // Update the recurring schedule rule
      const updateFields: Record<string, unknown> = {}
      if (updateData.day_of_week !== undefined)
        updateFields.day_of_week = updateData.day_of_week
      if (updateData.start_time !== undefined)
        updateFields.start_time = updateData.start_time
      if (updateData.end_time !== undefined)
        updateFields.end_time = updateData.end_time
      if (updateData.frequency !== undefined)
        updateFields.frequency = updateData.frequency
      if (updateData.effective_until !== undefined)
        updateFields.effective_until = updateData.effective_until
      if (updateData.location_text !== undefined)
        updateFields.location_text = normalizeLocationText(
          updateData.location_text
        )

      if (Object.keys(updateFields).length > 0) {
        const { error } = await supabase
          .from('recurring_schedules')
          .update(updateFields)
          .eq('id', id)

        if (error) return { success: false, error: error.message }
      }

      // Update professionals if provided
      if (professional_ids && professional_ids.length > 0) {
        // Remove existing and re-insert
        await supabase
          .from('schedule_professionals')
          .delete()
          .eq('recurring_schedule_id', id)

        const { error: profError } = await supabase
          .from('schedule_professionals')
          .insert(
            professional_ids.map((profId) => ({
              recurring_schedule_id: id,
              professional_id: profId,
            }))
          )

        if (profError) return { success: false, error: profError.message }
      }

      // Handle scope: if this_and_future, update future sessions
      if (scope === 'this_and_future') {
        const today = format(new Date(), 'yyyy-MM-dd')
        const sessionUpdate: Record<string, unknown> = {}
        if (updateData.start_time !== undefined)
          sessionUpdate.start_time = updateData.start_time
        if (updateData.end_time !== undefined)
          sessionUpdate.end_time = updateData.end_time
        if (updateData.location_text !== undefined)
          sessionUpdate.location_text = normalizeLocationText(
            updateData.location_text
          )

        if (Object.keys(sessionUpdate).length > 0) {
          await supabase
            .from('sessions')
            .update(sessionUpdate)
            .eq('recurring_schedule_id', id)
            .eq('is_exception', false)
            .gte('session_date', today)
            .eq('status', 'scheduled')
        }
      }

      // Fetch updated schedule
      const { data: updated, error: fetchError } = await supabase
        .from('recurring_schedules')
        .select('*')
        .eq('id', id)
        .single()

      if (fetchError) return { success: false, error: fetchError.message }

      return { success: true, data: updated as RecurringSchedule }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] })
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}

export function useDeactivateRecurringSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: DeactivateRecurringScheduleInput
    ): Promise<ScheduleOperationResult> => {
      // Deactivate the recurring schedule
      const { error } = await supabase
        .from('recurring_schedules')
        .update({ is_active: false })
        .eq('id', input.id)

      if (error) return { success: false, error: error.message }

      // Cancel future sessions if requested
      if (input.cancel_future_sessions) {
        const today = format(new Date(), 'yyyy-MM-dd')
        const { error: cancelError } = await supabase
          .from('sessions')
          .update({ status: 'cancelled' })
          .eq('recurring_schedule_id', input.id)
          .gte('session_date', today)
          .eq('status', 'scheduled')

        if (cancelError) {
          return { success: false, error: cancelError.message }
        }
      }

      return { success: true }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-schedules'] })
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
    },
  })
}
